import React, { useState, useEffect, useContext } from "react";
import { Button, Collapse, Radio, Upload, message } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  LockOutlined,
} from "@ant-design/icons";
import "./style.css";
import { useLocation, useNavigate } from "react-router-dom";
import GenericModalForm from "../../../../Components/Modals/GenericModalForm";
import GenericPageManager from "../../../../Components/PageManager/GenericPageManager";
import GenericModal from "../../../../Components/Modals/GenericModal";
import EmailIcon from "../../../../Assets/Icons/envelop.svg";
import SettingsUserIcon from "../../../../Assets/Icons/settings_user.svg";
import { UserContext } from "../../../../App";
import { handleUpdateUser } from "../../../../Utlis/UpdateUserState";
import { getRoleBadgeStyle } from "../../../../Utlis/RoleBadge";
import {
  getUserById,
  updateUser,
  deleteUser,
  updatePageAccess,
} from "../../../../API/Admin/UserManagement";
import { getRoles } from "../../../../API/Admin/MyProfile";
import InitialsAvatar from "../../../../Components/InitialsAvatar";
import usePageAccess from "../../../../Utlis/usePageAccess";

const PAGE_SLUGS = [
  "dashboard",
  "fleet-management",
  "subscription-payments",
  "fuel-vendors",
  "system-configuration",
  "transactions",
  "credits-billing",
  "reports-analytics",
  "approvals-requests",
  "qr-management",
  "user-management",
  "settings-permissions",
  "audit-trail",
];

const formatPageName = (key) =>
  key
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const pagesList = PAGE_SLUGS.map(formatPageName);

const PAGE_SLUG_BY_NAME = PAGE_SLUGS.reduce((acc, slug) => {
  acc[formatPageName(slug)] = slug;
  return acc;
}, {});

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const defaultAccess = {};
pagesList.forEach((page) => {
  defaultAccess[page] = "restrict";
});

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const MAX_FILENAME_LENGTH = 60;

const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
  if (file.name.length <= maxLength) return file;

  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
  const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
  const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

  return new File([file], `${truncatedBase}${ext}`, { type: file.type });
};

const UserDetails = () => {
  const { canEdit } = usePageAccess("user-management");
  const location = useLocation();
  const passedUser = location.state?.user;
  const navigate = useNavigate();
  const { userData, setUserData } = useContext(UserContext);

  const [user, setUser] = useState({
    name: passedUser?.user_name || "",
    email: passedUser?.email || "",
    role: passedUser?.role || "",
    avatar: null,
  });

  const [pageAccess, setPageAccess] = useState({ ...defaultAccess });
  // Role limits from the backend: pages the user's role restricts can't be
  // changed, and pages the role only grants View on can't go to View & Edit.
  const [disabledPages, setDisabledPages] = useState([]);
  const [viewOnlyPages, setViewOnlyPages] = useState([]);

  const applyRoleLimits = (data) => {
    if (!data?.disabled_pages) return;
    setDisabledPages(data.disabled_pages.map(formatPageName));
    setViewOnlyPages((data.view_only_pages || []).map(formatPageName));
  };
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);

  const fetchRoles = async () => {
    if (roleOptions.length) return;

    try {
      const res = await getRoles();
      const roles = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.results || [];

      if (Math.floor(res?.status / 100) === 2) {
        setRoleOptions(
          roles.map((role) => ({ label: role.name, value: role.id })),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load roles."),
      );
    }
  };

  useEffect(() => {
    if (isEditUserOpen) fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditUserOpen]);

  const currentRoleId = roleOptions.find((r) => r.label === user.role)?.value;

  // Until roles are fetched (lazily, on dropdown focus), fall back to the
  // user's actual role so the field isn't blank.
  const roleFieldOptions = roleOptions.length
    ? roleOptions
    : user.role
      ? [{ label: user.role, value: user.role }]
      : [];

  const currentRoleValue = roleOptions.length ? currentRoleId : user.role;

  useEffect(() => {
    if (!passedUser?.key) return;

    const fetchUser = async () => {
      try {
        const res = await getUserById(passedUser.key);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setUser({
            name: data.name,
            email: data.email,
            role: data.role,
            avatar: resolveAvatarUrl(data.avatar),
          });

          applyRoleLimits(data);
          const rawPageAccess = data.page_access || {};
          setPageAccess({
            ...defaultAccess,
            ...Object.fromEntries(
              Object.entries(rawPageAccess).map(([key, value]) => [
                formatPageName(key),
                value,
              ]),
            ),
          });
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load user."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load user."),
        );
      }
    };

    fetchUser();
  }, [passedUser?.key]);

  const handleBack = () => {
    navigate(-1);
  };

  const handlePageAccessChange = async (newPageAccess) => {
    const previousPageAccess = pageAccess;
    setPageAccess(newPageAccess);

    if (!passedUser?.key) return;

    const rawPageAccess = Object.fromEntries(
      Object.entries(newPageAccess).map(([key, value]) => [
        PAGE_SLUG_BY_NAME[key] || key,
        value,
      ]),
    );

    try {
      const res = await updatePageAccess(passedUser.key, rawPageAccess);

      if (Math.floor(res?.status / 100) === 2) {
        applyRoleLimits(res?.data?.data);
        if (String(userData?.id) === String(passedUser.key)) {
          handleUpdateUser(setUserData, {
            ...userData,
            page_access: rawPageAccess,
          });
        }
        message.success(
          getErrorMessage(res?.data, "Page access updated successfully"),
        );
      } else {
        setPageAccess(previousPageAccess);
        message.error(
          getErrorMessage(res?.data, "Failed to update page access."),
        );
      }
    } catch (err) {
      setPageAccess(previousPageAccess);
      message.error(
        err?.response?.data?.page_access
          ? "Page access can't exceed what the user's role allows."
          : getErrorMessage(
              err?.response?.data,
              "Failed to update page access.",
            ),
      );
    }
  };

  const handleEditUser = async (formValues) => {
    const payload = {
      name: formValues.fullName || user.name,
      email: formValues.email || user.email,
      ...(typeof formValues.role === "number" ? { role: formValues.role } : {}),
      ...(formValues.password ? { password: formValues.password } : {}),
    };

    try {
      const res = await updateUser(passedUser?.key, payload);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data || {};
        setUser((prev) => ({
          ...prev,
          name: data.name || payload.name,
          email: data.email || payload.email,
          role:
            data.role ||
            roleOptions.find((r) => r.value === payload.role)?.label ||
            prev.role,
        }));

        applyRoleLimits(data);
        if (data.page_access) {
          setPageAccess({
            ...defaultAccess,
            ...Object.fromEntries(
              Object.entries(data.page_access).map(([key, value]) => [
                formatPageName(key),
                value,
              ]),
            ),
          });

          if (String(userData?.id) === String(passedUser?.key)) {
            handleUpdateUser(setUserData, {
              ...userData,
              role: data.role || userData?.role,
              page_access: data.page_access,
            });
          }
        }
        message.success(
          getErrorMessage(res?.data, "User updated successfully"),
        );
        setIsEditUserOpen(false);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update user."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update user."),
      );
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteUser(passedUser?.key);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(res?.data, "User deleted successfully"),
        );
        setIsConfirmOpen(false);
        navigate("/user-management");
      } else {
        message.error(getErrorMessage(res?.data, "Failed to delete user."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete user."),
      );
    }
  };

  const handleImageUpload = async (file) => {
    const previewUrl = URL.createObjectURL(file);

    setUser((prev) => ({
      ...prev,
      avatar: previewUrl,
    }));

    if (!passedUser?.key) return false;

    const formData = new FormData();
    formData.append("avatar", shortenFileName(file));

    try {
      const res = await updateUser(passedUser.key, formData);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data || {};
        setUser((prev) => ({
          ...prev,
          avatar: resolveAvatarUrl(data.avatar) || previewUrl,
        }));
        message.success(
          getErrorMessage(res?.data, "Avatar updated successfully"),
        );
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update avatar."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update avatar."),
      );
    }

    return false;
  };

  return (
    <div className="user-details-container">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>

      {/* PROFILE CARD */}
      <div className="profile-card">
        <div className="profile-left">
          <div className="avatar">
            {user.avatar ? (
              <img src={user.avatar} alt="avatar" />
            ) : (
              <InitialsAvatar
                name={user.name}
                style={{ width: "100%", height: "100%", fontSize: 22 }}
              />
            )}
          </div>

          <div className="user-name-container">
            <div className="user-name">{user.name}</div>
            <div className="user-email">{user.email}</div>
            <div className="user-role">
              <span style={getRoleBadgeStyle(user.role)}>
                {user.role || "-"}
              </span>
            </div>

            {canEdit && (
              <Upload
                showUploadList={false}
                beforeUpload={handleImageUpload}
                accept="image/png, image/jpeg, image/jpg"
              >
                <Button size="small" className="upload-btn">
                  Upload Photo
                </Button>
              </Upload>
            )}
          </div>
        </div>

        {canEdit && (
          <Button
            icon={<EditOutlined />}
            onClick={() => setIsEditUserOpen(true)}
            className="edit-btn"
          >
            Edit Profile
          </Button>
        )}
      </div>

      {/* MANAGE PAGES */}
      <GenericPageManager
        pagesList={pagesList}
        pageAccess={pageAccess}
        setPageAccess={handlePageAccessChange}
        childTitle="Delete Account"
        childSubTitle="This action will remove the user. Continue?"
        childButtonContent="Delete"
        childButtonIcon={<DeleteOutlined />}
        onChildButtonPress={() => setIsConfirmOpen(true)}
        isCollapsible={true}
        readOnly={!canEdit}
        readOnlyTooltip={{
          title: "View only access",
          text: "You only have view rights on User Management, so you can't change this user's permissions.",
        }}
        disabledPages={disabledPages}
        viewOnlyPages={viewOnlyPages}
      />
      <GenericModalForm
        open={isEditUserOpen}
        title="Edit User"
        okText="Save Changes"
        fields={[
          {
            name: "fullName",
            label: "Full Name",
            placeholder: "Enter User Name",
            type: "input",
            prefixIcon: <UserOutlined />,
          },
          {
            name: "role",
            label: "Select Role",
            placeholder: "Select User Role",
            type: "select",
            options: roleFieldOptions,
            onFocus: fetchRoles,
            prefixIcon: <img src={SettingsUserIcon} alt="icon" />,
          },
          {
            name: "email",
            label: "Email",
            placeholder: "Enter User Email",
            type: "input",
            prefixIcon: <img src={EmailIcon} alt="icon" />,
            rules: [{ type: "email", message: "Enter a valid email address" }],
          },
          {
            name: "password",
            label: "Password",
            placeholder: "Leave blank to keep current password",
            type: "password",
            prefixIcon: <LockOutlined />,
            rules: [
              {
                validator: (_, value) => {
                  if (!value || value.length >= 6) return Promise.resolve();
                  return Promise.reject(
                    new Error("Password must be at least 6 characters"),
                  );
                },
              },
            ],
          },
          {
            name: "confirmPassword",
            label: "Confirm Password",
            placeholder: "ReType Password",
            type: "password",
            prefixIcon: <LockOutlined />,
            dependencies: ["password"],
            rules: [
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const password = getFieldValue("password");
                  if (!password || password === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ],
          },
        ]}
        initialValues={{
          fullName: user.name,
          email: user.email,
          role: currentRoleValue,
        }}
        cancelText="Cancel"
        onCancel={() => setIsEditUserOpen(false)}
        onOk={handleEditUser}
      />

      <GenericModal
        open={isConfirmOpen}
        title="Confirm User Delete"
        subtitle={`Are you sure you want to delete user? This action will immediately delete the record.`}
        // highlightText={selectedRow?.user}
        showReason={false}
        textareaLabel="Reason"
        textareaPlaceholder="Enter reason..."
        confirmText="Confirm Delete"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default UserDetails;
